/**
 * Owner: Haselton Baker Risk Group, LLC
 * Product: SP3 - SP3-Ui
 * Developer: Mike McGlone <mike@hbrisk.com>
 * Collaborators: Patrick Barnum <patrick@hbrisk.com>
 * Date: 11/21/17
 * Copyright All Rights Reserved
 */
// @flow
import React, { Component, Fragment } from 'react';
import classNames from 'classnames';
import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Polygon,
} from 'react-google-maps';
import { MarkerWithLabel } from 'react-google-maps/lib/components/addons/MarkerWithLabel';
import { compose, withProps } from 'recompose';
import config, { GOOGLE_MAPS_API_KEY } from 'support/config';
import googleMapURL from './googleMapURL';
import mapStyles from './mapStyles.json';
import uuidv4 from 'uuid/v4';
import scenarios from 'components/support/maps/scenarios';
import { label } from 'components/support/maps/Map.module.scss';
import concrete from 'components/support/maps/inventory/concrete.json';
import tallBuilding from 'components/support/maps/inventory/tallBuilding.json';
import tallBuildingCatIV from 'components/support/maps/inventory/tallBuildingCatIV.json';
import tallBuildingNewCode from 'components/support/maps/inventory/tallBuildingNewCode.json';
import tallBuildingResilient from 'components/support/maps/inventory/tallBuildingResilient.json';

const inventories = {
  concrete,
  tallBuilding,
  tallBuildingCatIV,
  tallBuildingNewCode,
  tallBuildingResilient,
};

const coloredMarkerDef = {
  svg: [
    '<svg viewBox="0 0 22 41" width="22px" height="41px" xmlns="http://www.w3.org/2000/svg">',
    '<path d="M 11,41 c -2,-20 -10,-22 -10,-30 a 10,10 0 1 1 20,0 c 0,8 -8,10 -10,30 z" fill="{fillColor}" stroke="#ffffff" stroke-width="1.5"/>',
    '<circle cx="11" cy="11" r="3"/>',
    '</svg>',
  ].join(''),
  anchor: { x: 11, y: 41 },
  size: { width: 22, height: 41 },
};

const getColoredMarkerSvg = color => coloredMarkerDef.svg.replace('{fillColor}', color);

const getColoredMarkerUri = color => `data:image/svg+xml,${encodeURIComponent(getColoredMarkerSvg(color))}`;

const getColoredMarkerIcon = color => ({
  url: getColoredMarkerUri(color),
  anchor: coloredMarkerDef.anchor,
  size: coloredMarkerDef.size,
  scaledSize: coloredMarkerDef.size,
});

const binColor = (value) => {
  if (value > 0.35) {
    return '#CB181D';
  }
  if (value > 0.2) {
    return '#FF0000';
  }
  if (value > 0.125) {
    return 'orange';
  }
  if (value > 0.03) {
    return 'yellow';
  }
  return '#46CB18';
};

class ToggleableMarkerWithLabel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showLabel: false,
    };
  }

  setShowLabel(showLabel) {
    this.setState({ showLabel });
  }

  render() {
    const {
      markerProps,
      google,
      messageComp,
      position,
      markerColor,
    } = this.props;

    const { showLabel } = this.state;
    const point = new google.maps.Point(0, 0);
    return (
      <MarkerWithLabel
        {...markerProps}
        position={position}
        icon={getColoredMarkerIcon(markerColor)}
        labelAnchor={point}
        labelClass={label}
        labelVisible={showLabel}
        onMouseOver={() => this.setShowLabel(true)}
        onMouseOut={() => this.setShowLabel(false)}
      >
        {messageComp}
      </MarkerWithLabel>
    );
  }
}

export const EnhancedGoogleMap = withGoogleMap(({
  scenario, inventory, markerProps, center, ...rest
}) => {
  const { scenario: scenarioData, pga } = scenarios[scenario];
  const inventoryData = inventories[inventory];
  const markers = inventoryData.map((building) => {
    const {
      lat,
      lng,
      building_type_id: buildingTypeId,
      mean_loss: meanLoss,
      year_of_construction: yearOfConstruction,
      num_stories: numStories,
      sul,
    } = building;

    const lossAtPGA = meanLoss[pga];
    const sulAtPGA = sul[pga];
    const color = binColor(lossAtPGA);
    return (
      <ToggleableMarkerWithLabel
        key={uuidv4()}
        /* eslint-disable no-undef */
        google={google}
        /* eslint-enable no-undef */
        markerColor={color}
        markerProps={markerProps}
        position={{ lat, lng }}
        opacity="1"
        messageComp={(
          <Fragment>
            <div>Building Type: {buildingTypeId}</div>
            <div>Year of Construction: {yearOfConstruction}</div>
            <div>Number of Stories: {numStories}</div>
            <div>Mean Loss: {`${Math.round(lossAtPGA * 100)}%`}</div>
            <div>90th Percentile Loss: {`${Math.round(sulAtPGA * 100)}%`}</div>
          </Fragment>
        )}
      />
    );
  });

  const codeToColor = {
    1: '#C2A5CF',
    2: '#A6CEE3',
    3: '#1F78B4',
    4: '#33A02C',
    5: '#B2DF8A',
    6: '#FFFF00',
    7: '#DEB900',
    8: '#E96900',
    9: '#E31A1C',
    10: '#9E0142',
  };

  return (
    <GoogleMap
      center={center}
      defaultOptions={
        {
          styles: mapStyles,
        }
      }
      {...rest}
    >
      { markers }
      {
        scenarioData.features.reduce((outerAcc, feature) => {
          const color = codeToColor[feature.properties.GRID_CODE];
          const { coordinates: latLngGroups } = feature.geometry;

          const newPolygons = latLngGroups.reduce((innerAcc, latLngGroup) => {
            const path = latLngGroup[0].map(
              (latLng) => {
                return ({
                  lat: latLng[1],
                  lng: latLng[0],
                });
              }
            );
            const newPolygon = (<Polygon
              key={uuidv4()}
              path={path}
              options={{
                fillColor: color,
                strokeColor: color,
                fillOpacity: 0.07,
              }}
            />);
            return [...innerAcc, newPolygon];
          }, []);
          return [...outerAcc, ...newPolygons];
        }, [])
      }
    </GoogleMap>
  );
});

type Props = {
  center: {
    lat: number,
    lng: number,
  },
  defaultZoom?: number,
  mapHeight?: number | string,
  markerProps?: Object,
  wrapperClassName?: string,
  wrapperHeight: number | string,
  scenario: Object,
  inventory: string,
}

const Map = ({
  scenario,
  inventory,
  wrapperHeight,
  wrapperClassName,
  mapHeight,
  markerProps,
  center,
  ...rest
}: Props) => (
  <EnhancedGoogleMap
    containerElement={
      <div className={classNames(wrapperClassName)} style={{ height: wrapperHeight }} />
      }
    mapElement={
      <div style={{ height: mapHeight }} />
      }
    markerProps={markerProps}
    center={center}
    scenario={scenario}
    inventory={inventory}
    {...rest}
  />
);

Map.defaultProps = {
  defaultZoom: 15,
  mapHeight: '100%',
  markerProps: {},
  wrapperClassName: '',
};

export default compose(
  withProps({
    googleMapURL: googleMapURL(config(GOOGLE_MAPS_API_KEY)),
    loadingElement: <div />,
  }),
  withScriptjs
)(Map);



// WEBPACK FOOTER //
// ./js/components/support/maps/Map.jsx